import httpClient from "@/services/http-client";
import AuthUserService from "./auth-user-service";
import { AxiosResponse } from "axios";
import User, { PartialUser } from "@/entities/user";
import { AsyncPaginatedRequest } from "@/services";
import HttpService from "../http-service";
import { LazyAsyncIter } from "@/iterators";
import { UserDTO, userFromDTO } from "./dto";
import Paginated, { pageMapperBuilder } from "@/pagination/paginated";
import { SnakeCasedProperties } from "type-fest";
export const authUserService = new AuthUserService(httpClient);

type PartialUserCreate = PartialUser & {
  password: string;
  passwordConfirmation: string;
};
type PartialUserUpdate = Partial<PartialUserCreate> & Partial<{ id: number }>;

type ApiPartialUser = Omit<
  SnakeCasedProperties<PartialUserCreate>,
  "enabled"
> & { enabled: 1 | 0 };

function partialUserCreateToApi(user: PartialUserCreate): ApiPartialUser {
  return {
    first_name: user.firstName,
    last_name: user.lastName,
    entity_id: user.entityId,
    role_id: user.roleId,
    email: user.email,
    password: user.password,
    password_confirmation: user.passwordConfirmation,
    enabled: user.enabled ? 1 : 0,
  };
}
function partialUserToUpdate(user: PartialUserUpdate) {
  return {
    first_name: user.firstName,
    last_name: user.lastName,
    role_id: user.roleId,
    password: user.password,
    enabled: user.enabled ? 1 : 0,
    email: user.email,
  };
}

export interface FilterUsersParams {
  first_name?: string;
  last_name?: string;
  email?: string;
  role_id?: number|string;
  entity_id?: number;
  search?: string;
  users_id?: number[];
}

export interface UserPasswordsUpdate {
  current_password: string;
  password: string;
  password_confirmation: string;
}

function partialUserPassworsUpdate(user: UserPasswordsUpdate) {
  return {
    current_password: user.current_password,
    password: user.password,
    password_confirmation: user.password_confirmation,
  };
}
export default class UserService extends HttpService {
  public async getUser(id: number): Promise<User> {
    const response = await this.httpClient.get<UserDTO>(`users/${id}`);
    return userFromDTO(response.data);
  }
  public getUsers(params: FilterUsersParams): LazyAsyncIter<Paginated<User>> {
    const apiParams: Omit<FilterUsersParams, "users_id"> & {
      users_id?: string;
    } = { ...params, users_id: undefined };
    if (params.users_id !== undefined) {
      apiParams.users_id = params.users_id.join(",");
    }
    const paginatedReqIter = new AsyncPaginatedRequest<UserDTO>(
      "get",
      "users",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();

    return new LazyAsyncIter(paginatedReqIter).map(
      pageMapperBuilder(userFromDTO)
    );
  }

  public createUser(user: PartialUserCreate): Promise<AxiosResponse<number>> {
    return this.httpClient.post("users", partialUserCreateToApi(user));
  }

  public updateUser(user: PartialUserUpdate): Promise<AxiosResponse<void>> {
    return this.httpClient.patch(`users/${user.id}`, partialUserToUpdate(user));
  }

  public delete(userId: number): Promise<AxiosResponse<void>> {
    return this.httpClient.delete(`users/${userId}`);
  }

  public updatePassword(
    userPasswords: UserPasswordsUpdate
  ): Promise<AxiosResponse<void>> {
    return this.httpClient.put(
      "/user/password",
      partialUserPassworsUpdate(userPasswords)
    );
  }
}
